import React, { useState, useEffect } from 'react';
import { StyleSheet, View, TextInput, Pressable, Platform } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import { useTheme } from '@/context/ThemeContext';
import { useAgentStore, useUIStore, useAuthStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import PropTypes from 'prop-types';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import { AGENT_ICON } from '@/constants/config';
import Icon from '@/components/Icon';
import Badge from '@/components/Typography/Badge';
import Text from '@/components/Typography/Text';

// Create styles outside component
const createStyles = (colors) => StyleSheet.create({
  menuItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  menuItemText: {
    color: colors.text_color_primary,
    fontSize: 14,
  },
  searchContainer: {
    padding: 0,
    position: 'relative',
    marginBottom: 5,
  },
  searchInput: {
    backgroundColor: colors.solid_color_tertiary,
    padding: 8,
    paddingLeft: 36,
    paddingRight: 38,
    borderRadius: 6,
    color: colors.text_color_primary,
  },
  searchIconContainer: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -7 }],
    zIndex: 1,
  },
  clearIconContainer: {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: [{ translateY: -7 }],
    zIndex: 1,
    cursor: 'pointer',
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleBadge: {
    marginLeft: 'auto',
  },
});

const AgentSelector = ({ agentId, onChange }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const pathname = usePathname();
  const router = useRouter();
  const startAgentWorkChat = useStartAgentWorkChat();
  const [isOpen, setIsOpen] = useState(false);
  const { isXS } = useResponsiveness();
  const agents = useAgentStore(state => state.agents);
  const featuredAgentIds = useAgentStore(state => state.featuredAgentIds);
  const isLoadingFeaturedAgents = useAgentStore(state => state.isLoadingFeaturedAgents);
  const isFetchedFeaturedAgents = useAgentStore(state => state.isFetchedFeaturedAgents);
  const searchResultIds = useAgentStore(state => state.searchResultIds);
  const isLoadingAgentSearch = useAgentStore(state => state.isLoadingAgentSearch);
  const searchError = useAgentStore(state => state.searchError);
  const fetchFeaturedAgents = useAgentStore(state => state.fetchFeaturedAgents);
  const searchAgents = useAgentStore(state => state.searchAgents);
  const resetSearch = useAgentStore(state => state.resetSearch);
  const isSidebarVisible = useUIStore(state => state.isSidebarVisible);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);

  // Get the current agent from the store
  const currentAgent = agents[agentId];

  // Close dropdown when route changes
  useEffect(() => {
    setIsOpen(false);
    onChange?.({ type: 'navigation', isOpen: false });
  }, [pathname, onChange]);

  // Close dropdown when sidebar visibility changes
  useEffect(() => {
    setIsOpen(false);
    onChange?.({ type: 'sidebar_change', isOpen: false });
  }, [isSidebarVisible, onChange]);

  // Fetch featured agents on mount if not already loaded
  useEffect(() => {
    if (!isLoadingFeaturedAgents && !isFetchedFeaturedAgents) {
      try {
        fetchFeaturedAgents();
      } catch (error) {
        console.error('Failed to fetch featured agents:', error);
      }
    }
  }, [fetchFeaturedAgents, isLoadingFeaturedAgents, isFetchedFeaturedAgents]);

  // Cleanup search when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      resetSearch();
    };
  }, []);

  // Handle search with debounce
  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Clear search if query is empty
    if (!text.trim()) {
      resetSearch();
      return;
    }
    
    const timeout = setTimeout(() => {
      if (text.trim()) {
        searchAgents(text);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    resetSearch();
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      // Clear search when closing
      setSearchQuery('');
      resetSearch();
    }
    onChange?.({ type: 'dropdown_state', isOpen: open });
  };

  const handleAgentSelect = (agent) => {
    startAgentWorkChat(agent, { source: 'agent_selector' });
    onChange?.({ type: 'agent_selected', agent });
  };

  // Get search results from the store
  const searchResults = searchResultIds.map(id => agents[id]).filter(Boolean);

  // Get featured agents or search results
  const availableAgents = searchQuery.trim()
    ? searchResults
    : featuredAgentIds.map(id => agents[id]).filter(Boolean);

  const styles = React.useMemo(() => createStyles(colors), [colors]);

  if (isLoadingFeaturedAgents) {
    return null;
  }

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger>
        <NavigationBarButton 
          icon={isXS && !currentAgent?.avatarPath ? AGENT_ICON : null}
          prependImage={currentAgent?.avatarPath}
          title={currentAgent?.name || t('nav.agentSelector.title')}
          tooltip={t('nav.agentSelector.title')}
          appendIcon={!isXS ? "chevron-down" : null}
          appendIconSize={!isXS ? "12px" : undefined}
          prependImageSize={!isXS ? "18px" : '20px'}
          text={!isXS ? currentAgent?.name || t('nav.agentSelector.title') : null}
          hoverBackgroundColor={colors.solid_color_tertiary}
          isActive={isOpen}
          style={{
            paddingHorizontal: isXS ? 6 : 8,
          }}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        style={{ minWidth: 200 }}
      >
        {Platform.OS === 'web' ? (
          <>
            <View style={styles.searchContainer}>
              <View style={styles.searchIconContainer}>
                <Icon
                  name="search"
                  size={14}
                  color={colors.text_color_secondary}
                />
              </View>
              <TextInput
                style={styles.searchInput}
                placeholder={t('nav.agentSelector.searchPlaceholder')}
                placeholderTextColor={colors.text_color_secondary}
                value={searchQuery}
                onChangeText={handleSearch}
              />
              {searchQuery.length > 0 && (
                <Pressable
                  style={styles.clearIconContainer}
                  onPress={handleClearSearch}
                >
                  <Icon
                    name="xmark"
                    size={14}
                    color={colors.text_color_secondary}
                  />
                </Pressable>
              )}
            </View>

            {!searchQuery.trim() && <DropdownMenuLabel text={t('nav.agentSelector.featured')} />}
            {isLoadingAgentSearch ? (
              <DropdownMenuItem
                title={t('nav.agentSelector.searching')}
                disabled
              />
            ) : searchError ? (
              <DropdownMenuItem
                title={t('nav.agentSelector.searchError')}
                disabled
              />
            ) : availableAgents.length === 0 && searchQuery.trim() ? (
              <DropdownMenuItem
                title={t('nav.agentSelector.noAgents')}
                disabled
              />
            ) : (
              availableAgents.map((agent) => {
                if (!agent) return null;

                return (
                  <DropdownMenuItem
                    key={agent.id}
                    imageUrl={agent.avatarPath}
                    icon={AGENT_ICON}
                    title={
                      <View style={styles.titleContainer}>
                        <Text>{agent.name}</Text>
                        {agent?.flags?.isPlusOnly && !isPlus && (
                          <Badge
                            text="PLUS"
                            onPress={() => handleAgentSelect(agent)}
                            icon="sparkles"
                            size="small"
                            isPlus
                          />
                        )}
                      </View>
                    }
                    onSelect={() => handleAgentSelect(agent)}
                    isActive={agent.id === agentId}
                  />
                );
              })
            )}
            <DropdownMenuItem
              icon={AGENT_ICON}
              title={t('nav.agentSelector.viewAll')}
              onSelect={() => {
                router.push(`/agents`);
                onChange?.({ type: 'view_all_selected' });
              }}
            />
          </>
        ) : (
          // Native menus are built from platform menu items, so they can't host
          // the search field or the PLUS badge. Featured agents are listed
          // directly and searching happens on the /agents screen behind View all.
          <>
            {availableAgents.length === 0 ? (
              <DropdownMenu.Item key="no-agents" disabled>
                <DropdownMenu.ItemTitle>{t('nav.agentSelector.noAgents')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            ) : (
              availableAgents.map((agent) => (
                <DropdownMenu.Item
                  key={agent.id}
                  onSelect={() => handleAgentSelect(agent)}
                >
                  <DropdownMenu.ItemTitle>
                    {agent?.flags?.isPlusOnly && !isPlus ? `${agent.name} (Plus)` : agent.name}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              ))
            )}
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              key="view-all"
              onSelect={() => {
                router.push(`/agents`);
                onChange?.({ type: 'view_all_selected' });
              }}
            >
              <DropdownMenu.ItemIcon ios={{ name: 'magnifyingglass', color: colors.text_color_primary }} />
              <DropdownMenu.ItemTitle>{t('nav.agentSelector.viewAll')}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

AgentSelector.propTypes = {
  agentId: PropTypes.string,
  onChange: PropTypes.func,
};

AgentSelector.displayName = 'AgentSelector';

export default React.memo(AgentSelector); 